// Parser: konverterar HTML-fragment till Block[] via DOMParser.
// Letar primärt efter rot-element med data-block-type, faller tillbaka till
// TextBlock-passthrough för okänd HTML. Designad att vara round-trip-säker
// med serialize.ts.

import {
  Block,
  BlockType,
  ButtonBlock,
  DEFAULT_DOC_SETTINGS,
  DividerBlock,
  EmailDocSettings,
  HeadingBlock,
  HeadingLevel,
  ImageBlock,
  TextAlign,
  TextBlock,
  TwoColumnsBlock,
  newBlockId,
} from './types';

// Returresultatet från parseHtml — block-listan plus de doc-settings som
// hittades i HTML:en (eller defaults om markören saknas).
export interface ParsedEmail {
  blocks: Block[];
  settings: EmailDocSettings;
}

const isHeadingLevel = (n: number): n is HeadingLevel => n === 1 || n === 2 || n === 3;
const isTextAlign = (v: string | null): v is TextAlign => v === 'left' || v === 'center' || v === 'right';

const parseHeading = (el: Element): HeadingBlock => {
  const levelAttr = parseInt(el.getAttribute('data-level') ?? '2', 10);
  const level: HeadingLevel = isHeadingLevel(levelAttr) ? levelAttr : 2;
  const alignAttr = el.getAttribute('data-align');
  const align: TextAlign = isTextAlign(alignAttr) ? alignAttr : 'left';
  const tag = el.querySelector(`h${level}, h1, h2, h3`);
  const text = tag?.textContent?.trim() ?? '';
  return { id: newBlockId(), type: 'heading', level, text, align };
};

const parseText = (el: Element): TextBlock => {
  // Text-blocket lagrar inre HTML av första <td>:n.
  const td = el.querySelector('td');
  const html = td?.innerHTML?.trim() ?? '';
  return { id: newBlockId(), type: 'text', html };
};

const parseImage = (el: Element): ImageBlock => {
  const img = el.querySelector('img');
  const src = img?.getAttribute('src') ?? '';
  const alt = img?.getAttribute('alt') ?? '';
  const widthAttr = img?.getAttribute('width');
  const width = widthAttr ? parseInt(widthAttr, 10) : undefined;
  // href kan ligga som data-attribut på root eller som omslutande <a>
  const dataHref = el.getAttribute('data-href');
  const linkEl = img?.closest('a');
  const href = dataHref ?? linkEl?.getAttribute('href') ?? undefined;
  // Full-bleed = padding 0 på td. Antingen explicit via data-full-bleed=1,
  // eller automatiskt om td:n har padding: 0 i style-attributet (för bakåt-
  // kompatibilitet med mallar som skrevs innan flaggan fanns).
  const fullBleedAttr = el.getAttribute('data-full-bleed') === '1';
  const td = el.querySelector('td');
  const tdStyle = td?.getAttribute('style') ?? '';
  const tdHasZeroPadding = /padding:\s*0\s*[;"]/i.test(tdStyle);
  const fullBleed = fullBleedAttr || tdHasZeroPadding;
  return {
    id: newBlockId(),
    type: 'image',
    src,
    alt,
    ...(width && Number.isFinite(width) ? { width } : {}),
    ...(href ? { href } : {}),
    ...(fullBleed ? { fullBleed: true } : {}),
  };
};

const parseButton = (el: Element): ButtonBlock => {
  const alignAttr = el.getAttribute('data-align');
  const align: TextAlign = isTextAlign(alignAttr) ? alignAttr : 'center';
  const bgColor = el.getAttribute('data-bg-color') ?? undefined;
  const textColor = el.getAttribute('data-text-color') ?? undefined;
  const link = el.querySelector('a');
  const label = link?.textContent?.trim() ?? '';
  const href = link?.getAttribute('href') ?? '#';
  return {
    id: newBlockId(),
    type: 'button',
    label,
    href,
    align,
    ...(bgColor ? { bgColor } : {}),
    ...(textColor ? { textColor } : {}),
  };
};

const parseDivider = (el: Element): DividerBlock => {
  const color = el.getAttribute('data-color') ?? undefined;
  return { id: newBlockId(), type: 'divider', ...(color ? { color } : {}) };
};

const parseTwoColumns = (el: Element): TwoColumnsBlock => {
  const leftTd = el.querySelector('[data-column="left"]');
  const rightTd = el.querySelector('[data-column="right"]');
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const left = leftTd ? parseChildren(leftTd) : [];
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const right = rightTd ? parseChildren(rightTd) : [];
  return { id: newBlockId(), type: 'twoColumns', left, right };
};

// Sträng som fallback-text-block: tar hela elementets outerHTML.
const wrapAsTextBlock = (html: string): TextBlock => ({
  id: newBlockId(),
  type: 'text',
  html,
});

// Parsa direktbarn av ett container-element. Används för rot och rekursivt
// för kolumners innehåll.
const parseChildren = (container: Element): Block[] => {
  const out: Block[] = [];
  const children = Array.from(container.children);
  if (children.length === 0) {
    // Ingen elementbarn → titta på textinnehåll
    const text = container.textContent?.trim();
    if (text) {
      out.push(wrapAsTextBlock(container.innerHTML));
    }
    return out;
  }
  children.forEach((child) => {
    const type = child.getAttribute('data-block-type') as BlockType | null;
    if (!type) {
      // Okänt rot-element — wrappa hela elementet som TextBlock
      out.push(wrapAsTextBlock(child.outerHTML));
      return;
    }
    switch (type) {
      case 'heading':
        out.push(parseHeading(child));
        break;
      case 'text':
        out.push(parseText(child));
        break;
      case 'image':
        out.push(parseImage(child));
        break;
      case 'button':
        out.push(parseButton(child));
        break;
      case 'divider':
        out.push(parseDivider(child));
        break;
      case 'twoColumns':
        out.push(parseTwoColumns(child));
        break;
      default:
        out.push(wrapAsTextBlock(child.outerHTML));
    }
  });
  return out;
};

// Hittar alla data-block-type-element på "topp-nivå" — dvs de som inte ligger
// inuti ett annat data-block-type-element. Två-kolumners inre block exkluderas
// här eftersom de hanteras rekursivt av parseTwoColumns.
const findTopLevelBlocks = (root: Element): Element[] => {
  const all = Array.from(root.querySelectorAll('[data-block-type]'));
  return all.filter((el) => !el.parentElement?.closest('[data-block-type]'));
};

// Parsa ett enskilt block-element till motsvarande Block-objekt.
const parseBlockElement = (el: Element): Block => {
  const type = el.getAttribute('data-block-type') as BlockType;
  switch (type) {
    case 'heading':
      return parseHeading(el);
    case 'text':
      return parseText(el);
    case 'image':
      return parseImage(el);
    case 'button':
      return parseButton(el);
    case 'divider':
      return parseDivider(el);
    case 'twoColumns':
      return parseTwoColumns(el);
    default:
      return wrapAsTextBlock(el.outerHTML);
  }
};

// Snabb hex/rgb-validering — vi tillåter bara strängar som ser ut som färger
// så att stökig HTML inte kan smyga in arbiträra värden i doc-settings.
const isColorLike = (v: string | null | undefined): v is string => {
  if (!v) return false;
  const trimmed = v.trim();
  return /^#[0-9a-f]{3,8}$/i.test(trimmed) || /^rgb/i.test(trimmed) || /^[a-z]+$/i.test(trimmed);
};

// Letar efter `data-email-doc`-markören och plockar ut färger + typsnitt om de finns.
// Annars sniffar vi den första `<td bgcolor="...">` i HTML:en — system-mallarna
// har samma envelope-mönster så det blir oftast rätt.
const extractDocSettings = (body: HTMLElement): EmailDocSettings => {
  const marker = body.querySelector('table[data-email-doc]');
  if (marker) {
    const bodyBg = marker.getAttribute('data-body-bg');
    const contentBg = marker.getAttribute('data-content-bg');
    const headingFont = marker.getAttribute('data-heading-font');
    const bodyFont = marker.getAttribute('data-body-font');
    return {
      bodyBgColor: isColorLike(bodyBg) ? bodyBg : DEFAULT_DOC_SETTINGS.bodyBgColor,
      contentBgColor: isColorLike(contentBg) ? contentBg : DEFAULT_DOC_SETTINGS.contentBgColor,
      headingFontFamily: headingFont ?? DEFAULT_DOC_SETTINGS.headingFontFamily,
      bodyFontFamily: bodyFont ?? DEFAULT_DOC_SETTINGS.bodyFontFamily,
    };
  }
  // Sniff: första td:n med bgcolor i envelope-position blir bodyBg, första
  // innre td:n med bgcolor blir contentBg. Typsnitt faller tillbaka till default.
  const tds = Array.from(body.querySelectorAll('td[bgcolor]'));
  const bodyBg = tds[0]?.getAttribute('bgcolor');
  const contentBg = tds
    .slice(1)
    .find((td) => td.getAttribute('bgcolor') !== bodyBg)
    ?.getAttribute('bgcolor');
  return {
    ...DEFAULT_DOC_SETTINGS,
    bodyBgColor: isColorLike(bodyBg) ? bodyBg : DEFAULT_DOC_SETTINGS.bodyBgColor,
    contentBgColor: isColorLike(contentBg) ? contentBg : DEFAULT_DOC_SETTINGS.contentBgColor,
  };
};

// Returnerar true om HTML:en är kompatibel med designläget — d.v.s. tomt eller
// taggat med `data-email-doc`-markören. Allt annat är "legacy" HTML som inte
// bör öppnas i designläge eftersom round-trip skulle korruptera strukturen.
export const isDesignModeCompatible = (html: string | null | undefined): boolean => {
  if (!html || !html.trim()) return true;
  return /data-email-doc\s*=/i.test(html);
};

// Huvudfunktion: konverterar HTML-sträng till block-lista + doc-settings.
// - Tom input → tom array + default-settings.
// - Markerad HTML (data-email-doc + data-block-type) hittas via querySelectorAll
//   så att envelope-rader (utan data-block-type) inte stör.
// - Legacy HTML (system-mall utan markers) → en stor TextBlock + sniffade settings.
export const parseHtml = (html: string): ParsedEmail => {
  if (!html || !html.trim()) {
    return { blocks: [], settings: { ...DEFAULT_DOC_SETTINGS } };
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<!DOCTYPE html><html><body>${html}</body></html>`, 'text/html');
  const { body } = doc;
  if (!body) return { blocks: [], settings: { ...DEFAULT_DOC_SETTINGS } };

  const settings = extractDocSettings(body);
  const topLevel = findTopLevelBlocks(body);
  if (topLevel.length > 0) {
    return { blocks: topLevel.map(parseBlockElement), settings };
  }

  // Ingen data-block-type-markering — falla tillbaka till att packa hela HTML:en som en TextBlock.
  return { blocks: parseChildren(body), settings };
};
